import { P2GBox, P2GRating, P2GText } from '@parcel2go/core-ui';
import { Stack } from '@mui/material';
import QuoteCard from '../card/quote-card';
import { BasketQuote, QuoteExtra } from '~/lib/types/basket-types';
import { ShopProviders } from '~/lib/types/edge-api-types';
import SkeletonLoader from '~/components/core/skeleton/skeleton-loader';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { memo, useMemo, lazy } from 'react';
import { QuoteFilterOptions, toQuoteGroups } from '~/lib/quotes';
import QuoteResultHeading from './quote-results-heading';
import { ConfigSetting, useDistance, useTaxName, useTenant } from '@parcel2go/npm-p2g-app';

const MemoizedQuoteCard = memo(QuoteCard);

const CmsContent = lazy(() => import('~/components/core/content/cms-content'));

export default function QuoteResults({
  quotes,
  onQuoteClick,
  shops,
  origin,
  destination,
  loading,
  onEnabledGeoLocation,
  incVat,
  filter,
  hideLastUsed,
  currentSelectedExtras
}: {
  quotes: BasketQuote[];
  onQuoteClick: (index: number, quote: BasketQuote, extras: string[]) => void;
  origin?: string;
  destination?: string;
  shops: ShopProviders;
  loading: boolean;
  incVat?: boolean;
  filter: QuoteFilterOptions;
  onEnabledGeoLocation?: () => void;
  hideLastUsed?: boolean;
  currentSelectedExtras?: QuoteExtra[];
}) {
  const { t } = useTranslation([TranslationNamespaces.QUOTE]);
  const { toDistance } = useDistance();
  const { settings } = useTenant();
  const taxName = useTaxName();

  const isDomestic =
    settings[ConfigSetting.DEFAULT_DESTINATION_COUNTRY] === destination &&
    settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY] === origin;

  const lastUsed = useMemo(() => {
    if (localStorage) {
      const lastService = localStorage.getItem('last_service');
      if (lastService && hideLastUsed !== false) {
        const quote = quotes.find((q) => q.service.slug === lastService);
        if (quote && quotes[0].service.slug !== lastService) {
          return (
            <QuoteCard
              incVat={incVat}
              quote={quote}
              onClick={(quote, extras) => onQuoteClick(quotes.indexOf(quote), quote, extras)}
              shops={shops[quote.service?.dropOffProviderGroup as string]}
              onEnabledGeoLocation={onEnabledGeoLocation}
              tab={t('LAST_USED', { defaultValue: 'LAST USED' })}
              currentSelectedExtras={currentSelectedExtras}
            />
          );
        }
      }
    }
  }, [hideLastUsed, quotes, incVat, shops, onEnabledGeoLocation, t, onQuoteClick, currentSelectedExtras]);

  const quoteGroups = useMemo(
    () => toQuoteGroups(filter, quotes, shops, isDomestic, toDistance, taxName),
    [filter, quotes, shops, isDomestic, toDistance, taxName]
  );

  if (loading) {
    return (
      <QuoteResultHeading translationKey="RESULT_LOADING_TITLE" defaultValue="Searching for services">
        <SkeletonLoader count={10} />
      </QuoteResultHeading>
    );
  }

  if (!loading && quotes.length === 0) {
    return (
      <QuoteResultHeading translationKey="RESULT_NO_RESULTS_TITLE" defaultValue="No services found">
        <P2GBox dataTestId="quotes-container" textAlign="center">
          <P2GText variant="h4">{t('NO_RESULT', { defaultValue: 'Sorry but we could not find any services' })}</P2GText>
        </P2GBox>

        <CmsContent contentKey="click-trans-banner" wrapper={(x) => <P2GBox mt={3}>{x}</P2GBox>} />
      </QuoteResultHeading>
    );
  }

  return (
    <Stack data-testid="quotes-container" gap={5}>
      {lastUsed && <div>{lastUsed}</div>}
      {quoteGroups.map((g, groupIndex) => (
        <QuoteResultHeading
          key={g.key}
          translationKey={g.translationKey}
          defaultValue={g.defaultTranslation}
          values={g.values}
          components={[<P2GText fontWeight="light" variant="h4" />]}
          adornment={filter.sortType === 'Rating' ? <P2GRating rating={g.values.rating as number} /> : undefined}
        >
          <Stack gap={2}>
            {g.quotes.map((quote, index) => (
              <div key={quote.service.slug} data-testid={`quote-${quote.service.slug}`}>
                <MemoizedQuoteCard
                  incVat={incVat}
                  quote={quote}
                  onClick={(quote, extras) => onQuoteClick(index, quote, extras)}
                  shops={shops[quote.service?.dropOffProviderGroup as string]}
                  onEnabledGeoLocation={onEnabledGeoLocation}
                  index={groupIndex == 0 ? undefined : index}
                  currentSelectedExtras={currentSelectedExtras}
                />
              </div>
            ))}
          </Stack>
        </QuoteResultHeading>
      ))}
    </Stack>
  );
}
